import { useCallback, useEffect, useMemo, useState } from 'react'
import { UsaStates } from 'usa-states'

import { useCenterStore } from '@caradasstore/CenterStore'
import { useUserStore } from '@caradasstore/UserStore'
import { CalibrationCenterLogo } from '@components/settings'
import dayjs from 'dayjs'
import { DATE_FORMATS } from '../../../constants'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import {
  AdasBox,
  AdasButton,
  AdasButtonGroup,
  AdasDatePicker,
  AdasSearchMenu,
  AdasTextField,
  AdasTypography
} from '@components/wrapper-components'
import { PenLine } from 'lucide-react'
import { Logger } from '../../../logger'
import { createCalibrationCenter, getCalibrationCenter } from '../../../api/api'
import { ccidFormatter, formatDate, phoneNumberFormat } from '../../../utils/utils'

export const EditCalibrationForm = () => {
  const user = useUserStore((store) => store.currentUser)
  const ccId = useCenterStore((store) => store.ccId)
  const setCenterDetail = useCenterStore((store) => store.setCenterDetail)
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const [isEdit, setIsEdit] = useState(false)
  const [isActive, setIsActive] = useState(true)
  const setLoading = useLoadingStore((store) => store.setLoading)

  const [calibrationCenter, setCalibrationCenter] = useState({
    name: '',
    website: '',
    email: '',
    phone: '',
    shop_address: '',
    shop_city: '',
    shop_state: '',
    shop_zip: '',
    shop_country: '',
    group_number: '',
    group_name: '',
    opening_date: '',
    support_lead: '',
    pbi_account_id: ''
  })
  const usStates = new UsaStates()

  const handleInputChange = (e) => {
    const value = getValueBasedOnField(e.target.value, e.target.name)
    setCalibrationCenter((prev) => {
      const calibrationCenter = {
        ...prev,
        [e.target.name]: value
      }
      return calibrationCenter
    })
  }

  const isBtnDisabled = useMemo(() => {
    const REQUIRED_FIELDS = ['name', 'email', 'phone', 'shop_address', 'shop_city', 'shop_state', 'shop_zip', 'shop_country']
    for (const key in calibrationCenter) {
      if (REQUIRED_FIELDS.includes(key) && (!calibrationCenter[key] || calibrationCenter[key].trim() === '')) {
        return true
      }
    }
    return false
  }, [calibrationCenter])

  const getValueBasedOnField = (value, fieldName) => {
    let result
    switch (fieldName) {
      case 'phone':
        result = phoneNumberFormat(value)
        break
      case 'opening_date':
        result = formatDate(value, DATE_FORMATS.ISO_DATE_STRING)
        break
      case 'group_number':
        result = formatGroupNumber(value)
        break
      default:
        result = value
    }
    return result
  }

  const formatGroupNumber = (value) => {
    return value.replace(/\D/g, '')
  }

  const fetchCalibrationCenter = useCallback(async () => {
    try {
      const ccResponse = await getCalibrationCenter({
        ccid: centerDetail.id
      })
      if (ccResponse?.status === 200) {
        if (ccResponse.data.opening_date) {
          ccResponse.data.opening_date = new Date(ccResponse.data.opening_date).toISOString()
        }
        setCenterDetail(ccResponse.data)
        setCalibrationCenter(ccResponse.data)
        setIsActive(ccResponse.data.is_active)
      }
    } catch (error) {
      Logger.error({ message: error, payload: { ccId } })
    }
  }, [setCenterDetail, centerDetail.id])

  useEffect(() => {
    setLoading(true)
    fetchCalibrationCenter().finally(() => setLoading(false))
  }, [fetchCalibrationCenter, isEdit])

  const handleCancel = () => {
    setCalibrationCenter(centerDetail)
    setIsEdit(false)
  }

  const handleSubmit = useCallback(async () => {
    const data = {
      ...centerDetail,
      ...calibrationCenter,
      group_name: calibrationCenter.group_name ? calibrationCenter.group_name.trim() : '',
      opening_date: formatDate(calibrationCenter.opening_date, DATE_FORMATS.ISO_DATE_TIME_STRING),
      is_active: isActive
    }

    const updateCCResponse = createCalibrationCenter({
      data
    })
    updateCCResponse.then((response) => {
      if (response?.status === 200) {
        setIsEdit(false)
        setCenterDetail(response.data)
      } else {
        setIsEdit(false)
      }
    })
  }, [centerDetail, calibrationCenter, isActive, setCenterDetail])

  const Field = ({ label, value, href }) => (
    <AdasBox>
      <AdasTypography className='text-[14px] text-muted-foreground mb-1'>{label}</AdasTypography>
      {href ? (
        <a href={href} className='text-[16px] font-semibold text-secondary hover:underline break-words'>
          {value || '—'}
        </a>
      ) : (
        <AdasTypography className='text-[16px] font-semibold text-foreground break-words'>{value || '—'}</AdasTypography>
      )}
    </AdasBox>
  )

  const CalibrationCenterInfo = (props) => {
    const ccData = props?.data
    const cityStateZip = [ccData.shop_city, ccData.shop_state].filter(Boolean).join(', ')
    const cityStateZipLine = [cityStateZip, ccData.shop_zip].filter(Boolean).join(' ')
    const openDate = formatDate(ccData.opening_date, DATE_FORMATS.ISO_DATE_STRING)
    return (
      <AdasBox className='rounded-2xl border border-border bg-white shadow-sm'>
        <AdasBox className='flex flex-col gap-6 p-6 sm:flex-row sm:items-start'>
          {/* Logo — dedicated left column */}
          {centerDetail?.image ? (
            <AdasBox className='flex h-[132px] w-[200px] shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-muted/40'>
              <img src={centerDetail.image} alt={`${ccData.name || 'Calibration Center'} logo`} className='h-full w-full object-contain' />
            </AdasBox>
          ) : (
            <AdasBox className='flex h-[132px] w-[200px] shrink-0 items-center justify-center rounded-xl bg-muted/60'>
              <AdasTypography className='text-[14px] text-muted-foreground'>Center logo</AdasTypography>
            </AdasBox>
          )}

          {/* Content — title, id/status, and the details grid (aligned with the title) */}
          <AdasBox className='min-w-0 flex-1'>
            <AdasTypography className='text-[32px] font-bold leading-tight text-foreground'>{ccData.name}</AdasTypography>
            <AdasBox className='mt-2 flex items-center gap-3'>
              <AdasTypography className='text-[16px] tracking-wide text-muted-foreground'>{ccidFormatter(ccId)}</AdasTypography>
              <span
                className={`inline-flex items-center rounded-full px-3 py-0.5 text-[13px] font-semibold ${
                  isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </AdasBox>

            <AdasBox className='mt-6 grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2'>
              <Field label='Address' value={ccData.shop_address} />
              <Field label='City / State / Zip' value={cityStateZipLine} />
              <Field label='Phone' value={phoneNumberFormat(ccData.phone)} />
              <Field label='Email' value={ccData.email} href={ccData.email ? `mailto:${ccData.email}` : undefined} />
              <Field label='Website' value={ccData.website} href={ccData.website || undefined} />
              <Field label='Open Date' value={openDate} />
              <Field label='Group #' value={ccData.group_number} />
              <Field label='Group Name' value={ccData.group_name} />
            </AdasBox>
          </AdasBox>
        </AdasBox>

        {/* Footer — edit action */}
        <hr className='border-border' />
        <AdasBox className='flex justify-end px-8 py-4'>
          <AdasButton
            variant='outlined'
            startIcon={<PenLine className='w-4 h-4' />}
            aria-label='Edit profile'
            onClick={() => setIsEdit(true)}
            className='rounded-lg px-5 font-medium'
          >
            Edit
          </AdasButton>
        </AdasBox>
      </AdasBox>
    )
  }

  if (!isEdit) {
    return <CalibrationCenterInfo data={calibrationCenter} />
  }

  return (
    <AdasBox className='rounded-xl border border-border bg-white p-8'>
      <CalibrationCenterLogo isEdit={isEdit} calibrationCenterData={centerDetail} onSuccess={fetchCalibrationCenter} />
      <AdasBox className='mt-4'>
        <AdasTextField
          onChange={handleInputChange}
          name='name'
          className='mb-[20px] mt-[10px]'
          id='name'
          label='Name'
          inputProps={{
            maxLength: 70
          }}
          variant='outlined'
          disabled={!user.is_admin}
          value={calibrationCenter.name}
          fullWidth={true}
          InputLabelProps={{
            shrink: true
          }}
        />
        <AdasTextField
          onChange={handleInputChange}
          name='shop_address'
          className='mb-[20px]'
          id='address'
          label='Address'
          variant='outlined'
          value={calibrationCenter.shop_address}
          fullWidth={true}
          InputLabelProps={{
            shrink: true
          }}
          inputProps={{
            maxLength: 55
          }}
        />
        <AdasBox className='flex flex-row mb-[20px]'>
          <AdasTextField
            onChange={handleInputChange}
            name='shop_city'
            className='mr-4'
            id='city'
            label='City'
            variant='outlined'
            value={calibrationCenter.shop_city}
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              maxLength: 20
            }}
          />
          <AdasSearchMenu
            fullWidth
            id='select-state'
            select
            label='State'
            variant='outlined'
            onChange={handleInputChange}
            name='shop_state'
            value={calibrationCenter.shop_state}
            options={usStates?.states?.map((state) => ({
              value: state.abbreviation,
              name: state.name
            }))}
          />
        </AdasBox>
        <AdasTextField
          onChange={handleInputChange}
          name='shop_zip'
          className='mb-[20px]'
          id='zip'
          label='Zip'
          variant='outlined'
          value={calibrationCenter.shop_zip}
          fullWidth={true}
          InputLabelProps={{
            shrink: true
          }}
          inputProps={{
            maxLength: 12
          }}
        />
        <AdasTextField
          onChange={handleInputChange}
          name='phone'
          className='mb-[20px]'
          id='phone'
          label='Phone'
          variant='outlined'
          value={calibrationCenter.phone ? phoneNumberFormat(calibrationCenter.phone) : ''}
          fullWidth={true}
          InputLabelProps={{
            shrink: true
          }}
        />
        <AdasTextField
          onChange={handleInputChange}
          name='email'
          className='mb-[20px]'
          id='email'
          label='Email'
          variant='outlined'
          value={calibrationCenter.email}
          fullWidth={true}
          InputLabelProps={{
            shrink: true
          }}
          inputProps={{
            maxLength: 128
          }}
        />
        <AdasTextField
          onChange={handleInputChange}
          name='website'
          className='mb-[20px]'
          id='name'
          label='Website (Optional)'
          variant='outlined'
          value={calibrationCenter.website}
          fullWidth={true}
          InputLabelProps={{
            shrink: true
          }}
          inputProps={{
            maxLength: 128
          }}
        />
        <AdasBox className='flex flex-row mb-[20px]'>
          <AdasDatePicker
            label='Open Date (Optional)'
            variant='outlined'
            name='opening_date'
            disabled={!user.is_admin}
            className='flex-1 mr-4'
            value={formatDate(calibrationCenter.opening_date, DATE_FORMATS.ISO_DATE_STRING) ?? ''}
            format={DATE_FORMATS.ISO_DATE_STRING}
            onChange={(newDate) => {
              const now = dayjs(newDate, DATE_FORMATS.ISO_DATE_STRING, true)
              if (now.isValid()) {
                let formattedDate = new Date(now.year(), now.month(), now.date())
                return handleInputChange({ target: { name: 'opening_date', value: formattedDate } })
              }
              return handleInputChange({ target: { name: 'opening_date', value: null } })
            }}
          />
          <AdasTextField
            onChange={handleInputChange}
            disabled={!user.is_admin}
            name='pbi_account_id'
            id='pbi_account_id'
            label='PBI Account (Optional)'
            variant='outlined'
            value={calibrationCenter.pbi_account_id ?? ''}
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              maxLength: 100
            }}
            className='flex-1'
          />
        </AdasBox>
        <AdasBox className='flex flex-row mb-[20px]'>
          <AdasTextField
            onChange={handleInputChange}
            disabled={!user.is_admin}
            name='support_lead'
            id='support_lead'
            label='Support Lead (Optional)'
            variant='outlined'
            value={calibrationCenter.support_lead}
            className='flex-1 mr-4'
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              maxLength: 60
            }}
          />
        </AdasBox>
        <AdasBox className='flex flex-row mb-[20px]'>
          <AdasTextField
            onChange={handleInputChange}
            disabled={!user.is_admin}
            name='group_number'
            id='group_number'
            label='Group # (Optional)'
            variant='outlined'
            value={calibrationCenter.group_number ?? ''}
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              maxLength: 3,
              inputMode: 'numeric',
              pattern: '\\d*'
            }}
            className='flex-1'
          />
          <AdasTextField
            onChange={handleInputChange}
            disabled={!user.is_admin}
            name='group_name'
            id='group_name'
            label='Group Name (Optional)'
            variant='outlined'
            value={calibrationCenter.group_name ?? ''}
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              maxLength: 30
            }}
            className='flex-1'
          />
        </AdasBox>
        {user.is_admin && (
          <AdasBox>
            <AdasButtonGroup variant='contained'>
              <AdasButton onClick={() => setIsActive(true)} variant={isActive ? 'contained' : 'outlined'}>
                Active
              </AdasButton>
              <AdasButton onClick={() => setIsActive(false)} variant={!isActive ? 'contained' : 'outlined'}>
                Inactive
              </AdasButton>
            </AdasButtonGroup>
          </AdasBox>
        )}
        <AdasBox className='overflow-auto my-[20px] mx-0'>
          <AdasBox className='float-right'>
            <AdasButton variant='outlined' aria-label='cancel' color='primary' onClick={handleCancel} className='mr-[10px]'>
              Cancel
            </AdasButton>
            <AdasButton variant='contained' type='submit' disabled={isBtnDisabled} aria-label='save' color='primary' onClick={handleSubmit}>
              Save
            </AdasButton>
          </AdasBox>
        </AdasBox>
      </AdasBox>
    </AdasBox>
  )
}
